/**
 * BrandingContext — injects org CSS variable overrides (b2b-3-1).
 *
 * When a user belongs to an organisation with custom branding, this context
 * applies the org's primary_color and accent_color as CSS custom properties
 * on the <html> element so Tailwind CSS variable-based classes pick them up.
 *
 * Defaults (my investments palette) are left as-is when no org branding
 * is available.
 */
import React, { createContext, useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrgBranding {
  app_name: string;
  primary_color: string;
  accent_color: string;
  logo_url: string | null;
  logo_dark_url: string | null;
  login_bg_url: string | null;
}

interface BrandingContextValue {
  branding: OrgBranding | null;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  isLoading: false,
});

// ---------------------------------------------------------------------------
// Default branding palette (my investments)
// ---------------------------------------------------------------------------

const MY_INVESTMENTS_DEFAULTS: OrgBranding = {
  app_name: 'my investments',
  primary_color: '#1DB954',
  accent_color: '#F0A500',
  logo_url: null,
  logo_dark_url: null,
  login_bg_url: null,
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  const { data: branding, isLoading } = useQuery<OrgBranding>({
    queryKey: ['org-branding'],
    queryFn: () => api.request<OrgBranding>('/api/v1/orgs/branding'),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 min
    retry: false,
  });

  // Apply CSS variables to <html> root element
  useEffect(() => {
    const palette = branding ?? MY_INVESTMENTS_DEFAULTS;
    const root = document.documentElement;

    // Convert hex to individual channel values for Tailwind CSS variable usage
    root.style.setProperty('--color-primary', palette.primary_color);
    root.style.setProperty('--color-accent', palette.accent_color);

    // Also expose as RGB channels for opacity utilities
    const primaryRgb = hexToRgb(palette.primary_color);
    const accentRgb = hexToRgb(palette.accent_color);

    if (primaryRgb) {
      root.style.setProperty('--color-primary-r', String(primaryRgb.r));
      root.style.setProperty('--color-primary-g', String(primaryRgb.g));
      root.style.setProperty('--color-primary-b', String(primaryRgb.b));
    }

    if (accentRgb) {
      root.style.setProperty('--color-accent-r', String(accentRgb.r));
      root.style.setProperty('--color-accent-g', String(accentRgb.g));
      root.style.setProperty('--color-accent-b', String(accentRgb.b));
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding: branding ?? null, isLoading }}>
      {children}
    </BrandingContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = /^#([0-9A-Fa-f]{6})$/.exec(hex);
  if (!match) return null;
  const int = parseInt(match[1], 16);
  return {
    r: (int >> 16) & 0xff,
    g: (int >> 8) & 0xff,
    b: int & 0xff,
  };
}
